import DeleteIcon from "@mui/icons-material/Delete";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import { BADGE_PALETTE, getToolStatusDisplay } from "../helpers/tool-status";
import {
  ADDITIONAL_TOOL_PRICE_EUR,
  App,
  Plan,
  Subscription,
} from "../types/entities";

export interface ReportsTableProps {
  apps: App[];
  plans: Plan[];
  subscription?: Subscription | null;
  isSwitchingClient: boolean;
  isCurrentUserOwner: boolean;
  isCurrentUserAdminOrOwner: boolean;
  onOpenReport: (appId: string) => void;
  onActivateTool: (appId: string) => void;
  onOpenSelectPlan: () => void;
  onDelete: (reportId: string) => void;
  language: SupportedLanguage;
}

export const ReportsTable = ({
  apps,
  plans,
  subscription,
  isSwitchingClient,
  isCurrentUserOwner,
  isCurrentUserAdminOrOwner,
  onOpenReport,
  onActivateTool,
  onOpenSelectPlan,
  onDelete,
  language,
}: ReportsTableProps) => {
  const [anchorEl, setAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    appId: string
  ) => {
    // React nulls currentTarget after the handler returns, before a deferred updater runs.
    const target = event.currentTarget;
    setAnchorEl((prev) => ({ ...prev, [appId]: target }));
  };

  const handleCloseMenu = (appId: string) => {
    setAnchorEl((prev) => ({ ...prev, [appId]: null }));
  };

  const handleDeleteClick = (appId: string) => {
    setPendingDeleteId(appId);
    setIsDeleteDialogOpen(true);
    handleCloseMenu(appId);
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return;
    setIsDeleting(true);
    onDelete(pendingDeleteId);
    setIsDeleteDialogOpen(false);
    setPendingDeleteId(null);
    setIsDeleting(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setPendingDeleteId(null);
    setIsDeleting(false);
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "25%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.name"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "20%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.created"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "25%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.owner"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "25%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.useCase"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "20%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.status"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "25%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.reports.table.columns.actions"
                )}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isSwitchingClient ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                  <CircularProgress size={40} sx={{ color: "#3ec6ff" }} />
                </TableCell>
              </TableRow>
            ) : apps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.reports.table.noReports"
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              apps.map((app) => {
                const currentPlan = subscription
                  ? plans.find((p) => p.key === subscription.planKey)
                  : undefined;
                const status = getToolStatusDisplay(
                  app,
                  subscription ?? null,
                  currentPlan ?? null,
                  ADDITIONAL_TOOL_PRICE_EUR,
                  apps,
                  language
                );

                const hasNoAccess = app.hasAccess === false;
                const canOpen = Boolean(app.url) && !hasNoAccess;
                const noAccessTooltip = hasNoAccess
                  ? getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.reports.table.tooltips.noAccess"
                    )
                  : "";

                return (
                  <TableRow key={app.id} hover>
                    <TableCell
                      sx={{
                        color: "#333",
                        textAlign: "left",
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          component="span"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {app.name}
                        </Box>
                        {hasNoAccess && (
                          <Tooltip title={noAccessTooltip} placement="top">
                            <LockOutlinedIcon
                              aria-label={noAccessTooltip}
                              sx={{
                                fontSize: 18,
                                color: "#999",
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ color: "#666", textAlign: "left" }}>
                      {new Date(app.createdAt).toLocaleString()}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#666",
                        textAlign: "left",
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {app.ownerFirstName && app.ownerLastName
                        ? `${app.ownerFirstName} ${app.ownerLastName}`
                        : app.ownerEmail || "N/A"}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#666",
                        textAlign: "left",
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {app.useCase
                        ? getLocalizedMessage(
                            language,
                            `useCase.${app.useCase}`
                          )
                        : "N/A"}
                    </TableCell>

                    <TableCell sx={{ textAlign: "left" }}>
                      <Chip
                        label={status.badge}
                        size="small"
                        sx={{
                          mb: 0.5,
                          bgcolor: BADGE_PALETTE[status.badgeTone].bg,
                          color: BADGE_PALETTE[status.badgeTone].color,
                          fontWeight: 500,
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {status.context}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ color: "#666", textAlign: "left" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 2,
                        }}
                      >
                        {status.primaryAction === "open" ? (
                          <Tooltip title={noAccessTooltip} placement="top">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onOpenReport(app.id)}
                                disabled={!canOpen}
                                sx={{
                                  borderColor: canOpen ? "#3ec6ff" : "#ccc",
                                  color: canOpen ? "#1da1f2" : "#999",
                                  borderRadius: 2,
                                  px: 2,
                                  py: 0.5,
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  textTransform: "none",
                                  boxShadow: "none",
                                  "&:hover": {
                                    borderColor: canOpen ? "#1da1f2" : "#ccc",
                                    bgcolor: canOpen
                                      ? "rgba(62,198,255,0.08)"
                                      : "transparent",
                                  },
                                }}
                              >
                                {getLocalizedMessage(
                                  language,
                                  `homePortalPage.tabs.reports.table.actions.${app.url ? "open" : "noUrl"}`
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : status.primaryAction === "activate" ? (
                          <Tooltip
                            title={
                              !isCurrentUserOwner
                                ? getLocalizedMessage(
                                    language,
                                    "homePortalPage.tabs.reports.table.tooltips.contactAccountOwnerToActivate"
                                  )
                                : ""
                            }
                            placement="top"
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!isCurrentUserOwner}
                                onClick={() => onActivateTool(app.id)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 2,
                                  fontSize: "0.8rem",
                                  bgcolor: "#ff8c00",
                                  color: "#fff",
                                  boxShadow: "none",
                                  "&:hover": { bgcolor: "#e67e00" },
                                }}
                              >
                                {getLocalizedMessage(
                                  language,
                                  "homePortalPage.tabs.reports.table.actions.activate"
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={
                              !isCurrentUserOwner
                                ? getLocalizedMessage(
                                    language,
                                    "homePortalPage.tabs.reports.table.tooltips.contactAccountOwnerToSelectPlan"
                                  )
                                : ""
                            }
                            placement="top"
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!isCurrentUserOwner}
                                onClick={onOpenSelectPlan}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 2,
                                  fontSize: "0.8rem",
                                  bgcolor: "#ff8c00",
                                  color: "#fff",
                                  boxShadow: "none",
                                  "&:hover": { bgcolor: "#e67e00" },
                                }}
                              >
                                {getLocalizedMessage(
                                  language,
                                  "homePortalPage.tabs.reports.table.actions.selectPlan"
                                )}
                              </Button>
                            </span>
                          </Tooltip>
                        )}

                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, app.id)}
                          sx={{ color: "#666" }}
                        >
                          <MoreVertIcon />
                        </IconButton>

                        <Menu
                          anchorEl={anchorEl[app.id]}
                          open={Boolean(anchorEl[app.id])}
                          onClose={() => handleCloseMenu(app.id)}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          <MenuItem
                            onClick={() => handleDeleteClick(app.id)}
                            disabled={!isCurrentUserAdminOrOwner}
                            sx={{
                              color: "#d32f2f",
                              "&:hover": { backgroundColor: "#ffebee" },
                              "&.Mui-disabled": { color: "#ccc" },
                            }}
                          >
                            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                            {getLocalizedMessage(
                              language,
                              "homePortalPage.tabs.reports.table.actions.delete"
                            )}
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {getLocalizedMessage(
            language,
            "homePortalPage.tabs.reports.deleteDialog.title"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.reports.deleteDialog.message1"
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.reports.deleteDialog.message2"
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            {getLocalizedMessage(language, "dialogActions.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {getLocalizedMessage(language, "dialogActions.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
